import {Injectable, inject} from '@angular/core';
import {
  PaymentProvider,
  PaymentProviderSettingDefinition,
} from '@gatewee/types/provider';
import {TranslateService} from '@ngx-translate/core';

export type EnabledProvidersInput =
  | PaymentProvider[]
  | Partial<Record<PaymentProvider | string, boolean>>
  | null
  | undefined;

/**
 * Helper class for working with payment providers
 * Provides methods to get formatted provider lists for select dropdowns
 */

@Injectable()
export class ProviderHelper {
  private translate = inject(TranslateService);

  private readonly allProviders = Object.values(PaymentProvider);

  /**
   * Normalize enabled providers from array or boolean map (same shape as paymentMethods).
   */
  resolveEnabledProviders(input: EnabledProvidersInput): PaymentProvider[] {
    if (input === null || input === undefined) {
      return [];
    }

    if (Array.isArray(input)) {
      return input.filter((provider): provider is PaymentProvider =>
        this.isPaymentProvider(provider),
      );
    }

    if (typeof input === 'object') {
      return Object.entries(input)
        .filter(([, enabled]) => enabled === true)
        .map(([provider]) => provider)
        .filter((provider): provider is PaymentProvider =>
          this.isPaymentProvider(provider),
        );
    }

    return [];
  }

  private isPaymentProvider(value: unknown): value is PaymentProvider {
    return (
      typeof value === 'string' &&
      this.allProviders.includes(value as PaymentProvider)
    );
  }

  private loadEnabledFromLocalStorage(): PaymentProvider[] {
    const businessRules = localStorage.getItem('businessRules');
    if (!businessRules) {
      return [];
    }

    try {
      const businessRulesObject = JSON.parse(businessRules) as {
        enabledProviders?: EnabledProvidersInput;
      };
      return this.resolveEnabledProviders(businessRulesObject.enabledProviders);
    } catch (error) {
      console.error('Error parsing businessRules from localStorage:', error);
      return [];
    }
  }

  private resolveEnabledList(
    enabledProviders?: EnabledProvidersInput,
  ): PaymentProvider[] {
    if (enabledProviders === undefined || enabledProviders === null) {
      const fromStorage = this.loadEnabledFromLocalStorage();
      return fromStorage.length > 0 ? fromStorage : this.allProviders;
    }

    if (
      !Array.isArray(enabledProviders) &&
      typeof enabledProviders === 'object'
    ) {
      return this.resolveEnabledProviders(enabledProviders);
    }

    if (Array.isArray(enabledProviders)) {
      if (enabledProviders.length === 0) {
        const fromStorage = this.loadEnabledFromLocalStorage();
        return fromStorage.length > 0 ? fromStorage : this.allProviders;
      }

      return this.resolveEnabledProviders(enabledProviders);
    }

    return this.allProviders;
  }

  /**
   * Get formatted select items for payment providers
   * @param scope - Filter by 'onboarding' or 'payments' scope
   * @param enabledProviders - Optional enabled providers (array or boolean map). Empty array falls back to localStorage.
   * @param settings - Optional provider settings definition, if not provided will check localStorage
   * @returns Array of select items with label and value
   */
  getSelectItems(
    scope: 'onboarding' | 'payments' = 'payments',
    enabledProviders?: EnabledProvidersInput,
    settings?: PaymentProviderSettingDefinition,
  ): {label: string; value: string}[] {
    let enabled = this.resolveEnabledList(enabledProviders);

    if (scope === 'onboarding') {
      if (!settings) {
        const businessRulesStr = localStorage.getItem('BusinessRules');
        if (businessRulesStr) {
          try {
            settings = JSON.parse(
              businessRulesStr,
            ) as PaymentProviderSettingDefinition;
          } catch (error) {
            console.error(
              'Error parsing BusinessRules from localStorage:',
              error,
            );
          }
        }
      }
      enabled = enabled.filter(
        (provider: PaymentProvider) =>
          settings?.[provider]?.onboarding === true,
      );
    }

    return enabled.map((provider: PaymentProvider) => ({
      label: this.translate.instant(`enums.paymentProvider.${provider}`),
      value: provider,
    }));
  }

  /**
   * Get the translated label for a specific provider
   * @param provider - The payment provider
   * @returns Translated provider label
   */
  getProviderLabel(provider: PaymentProvider): string {
    return this.translate.instant(`enums.paymentProvider.${provider}`);
  }

  /**
   * Check if a provider is enabled for a specific scope
   * @param provider - The payment provider to check
   * @param scope - The scope to check ('onboarding' or 'payments')
   * @param settings - Optional provider settings definition
   * @returns True if the provider is enabled for the scope
   */
  isProviderEnabled(
    provider: PaymentProvider,
    scope: 'onboarding' | 'payments' = 'payments',
    settings?: PaymentProviderSettingDefinition,
  ): boolean {
    const items = this.getSelectItems(scope, [provider], settings);
    return items.length > 0;
  }
}
